import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { isEmpty } from 'lodash-es';

import { useUserState } from '~coreModules/core/js/composables/useUserState';
import { useGetters, useState } from '~coreModules/core/js/composables/vuexHelpers';
import useMeta from '~coreModules/core/js/seo/composables/use-meta';
import { useRuntimeConfig } from '~coreModules/core/js/composables/useRuntimeConfig';

import { getProductModuleNameFromRoute } from '~coreRoutes/product/js/product-utils';
import { URBN_BRANDS } from '~coreModules/core/js/constants';

import { NUULY_DEPARTMENT_PLUS } from '~routes/product/js/product-constants';
import { IMAGE_PRESETS } from '~modules/core/js/constants';
import { SITE_SETTINGS_MODULE_NAME } from '~modules/site-settings/js/site-settings-store';

export default function useJoinMeta() {
    const { t } = useI18n();
    const route = useRoute();
    const userState = useUserState();
    const runtimeConfig = useRuntimeConfig();
    const productModuleName = computed(() => getProductModuleNameFromRoute(route));

    const { currency } = useState(SITE_SETTINGS_MODULE_NAME, ['currency']);
    const { productDetails, colorCode } = useState(productModuleName.value, [
        'productDetails',
        'colorCode',
    ]);

    const { selectedChoice, isProductSoldOut } = useGetters(productModuleName.value, [
        'selectedChoice',
        'isProductSoldOut',
    ]);

    const { defaultTitle } = runtimeConfig.siteMeta;

    const brandName = computed(() => {
        const brandUrls = productDetails.value?.brandUrls || [];
        if (isEmpty(brandUrls)) return '';

        if (brandUrls.length === 1) {
            return brandUrls[0].displayName;
        }

        const filteredBrandUrls = brandUrls.filter(brand => !URBN_BRANDS.includes(brand.displayName));
        if (isEmpty(filteredBrandUrls)) return '';

        return filteredBrandUrls[0].displayName;
    });
    const displayName = computed(() => productDetails.value?.displayName || null);
    const styleNumber = computed(() => productDetails.value?.styleNumber || '');
    const choiceId = computed(() => selectedChoice.value?.choiceId || '');
    const msrpValue = computed(() => {
        const val = productDetails.value?.msrpValue;
        return val ? `${val}.00` : null;
    });
    const maternityFacet = computed(() => productDetails.value?.facets?.maternity?.[0]);
    const lifestyleFacet = computed(() => productDetails.value?.facets?.lifestyles?.[0]);
    const plusSizeFacet = computed(() => {
        const nuulyDepartments = productDetails.value?.facets?.nuulyDepartment || [];
        return nuulyDepartments.find(department => department === NUULY_DEPARTMENT_PLUS);
    });
    const metaDescription = computed(() => t('meta__pdp--description', [displayName.value]));
    const metaImage = computed(() => {
        const imagePreset = IMAGE_PRESETS.product.ogImage;

        if (isEmpty(selectedChoice.value?.imageUrls)) return '';

        return `${selectedChoice.value?.imageUrls[0]}?${imagePreset}`;
    });

    function getPageTitle() {
        // Fallback to default title
        if (isEmpty(displayName.value)) return false;

        return t(defaultTitle, [displayName.value?.substring?.(0, 50)]);
    }

    function getMetaTags() {
        let metaTags = [{
            property: 'og:title',
            content: displayName.value,
        }, {
            name: 'description',
            content: metaDescription.value,
        }, {
            property: 'og:description',
            content: metaDescription.value,
        }, {
            property: 'og:image',
            content: metaImage.value,
        }, {
            property: 'og:ttl',
            content: '2592000',
        }, {
            property: 'og:type',
            content: 'product',
        }, {
            property: 'og:price:currency',
            content: currency.value,
        }, {
            property: 'product:price:currency',
            content: currency.value,
        }, {
            property: 'og:price:amount',
            content: msrpValue.value,
        }, {
            property: 'product:price:amount',
            content: msrpValue.value,
        }, {
            property: 'product:brand',
            content: brandName.value,
        }, {
            property: 'product:retailer_item_id',
            content: choiceId.value,
        }, {
            property: 'product:item_group_id',
            content: styleNumber.value,
        }, {
            property: 'product:category',
            content: 'Apparel & Accessories > Clothing',
        }, {
            property: 'product:availability',
            content: isProductSoldOut.value ? 'out of stock' : 'in stock',
        }, {
            property: 'product:condition',
            content: 'refurbished',
        }, {
            ...(maternityFacet.value && {
                property: 'product:custom_label_0',
                content: maternityFacet.value,
            }),
        }, {
            ...(lifestyleFacet.value && {
                property: 'product:custom_label_1',
                content: lifestyleFacet.value,
            }),
        }, {
            ...(plusSizeFacet.value && {
                property: 'product:custom_label_2',
                content: plusSizeFacet.value,
            }),
        }];

        if (process.env.VUE_ENV === 'server') {
            metaTags = [...metaTags, {
                property: 'og:url',
                content: `${userState.baseUrl}${route.path}?color=${colorCode.value}`,
            }];
        }

        return metaTags;
    }

    useMeta({ getMetaTags, getPageTitle });
}
